import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  ICreateTratamientoEvento,
  IQueryParam,
  estadoEvento,
} from 'modelos/src';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { TratamientoEventosService } from '../../../../../auxiliares/servicios/http/tratamientos-eventos.service';
import { EventosGenericosHttpService } from '../../../../../auxiliares/servicios/http/eventos-genericos.service';

export interface ITratamientoGrupalData {
  /** Nombre del grupo, para el encabezado */
  titulo: string;
  /** Eventos del grupo que están cargados en la página */
  idsEventos: string[];
  /**
   * Query del grupo completo (filtro base + personalizado + grupo). Presente
   * sólo si el criterio de agrupación es expresable del lado del servidor.
   */
  query?: IQueryParam | null;
  /** Total en la base cuando el listado está truncado */
  totalEnBase?: number;
}

/**
 * Tratamiento de un grupo de eventos.
 *
 * A diferencia de `tratamiento-generico`, no muestra ficha de entidad: un grupo
 * por zona o por cliente mezcla dispositivos distintos y no hay un domicilio,
 * un mapa ni un último reporte que representen al conjunto. Deja un mensaje y
 * un estado, que es lo que se pidió.
 *
 * El tratamiento es UNO solo con todos los eventos: `ITratamientoEvento.idsEventos`
 * ya es un array en el modelo, no hizo falta tocar el esquema.
 */
@Component({
  selector: 'app-tratamiento-grupal',
  standalone: true,
  imports: [
    AuxiliaresModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
  ],
  templateUrl: './tratamiento-grupal.component.html',
  styleUrls: ['./tratamiento-grupal.component.scss'],
})
export class TratamientoGrupalComponent {
  public data = inject<ITratamientoGrupalData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<TratamientoGrupalComponent>>(MatDialogRef);
  private service = inject(TratamientoEventosService);
  private eventosService = inject(EventosGenericosHttpService);
  public helper = inject(HelperService);

  public loading = false;

  public readonly estados: estadoEvento[] = [
    'En Atención',
    'Liberada',
    'Finalizada',
  ];

  public form = new FormGroup({
    nota: new FormControl<string>('', Validators.required),
    notaInterna: new FormControl<string>(''),
    estado: new FormControl<estadoEvento>('Finalizada', Validators.required),
  });

  public get cantidadCargada(): number {
    return this.data?.idsEventos?.length ?? 0;
  }

  /** true si hay más eventos en la base que los cargados en la página */
  public get truncado(): boolean {
    return (
      !!this.data?.totalEnBase && this.data.totalEnBase > this.cantidadCargada
    );
  }

  /**
   * Cuántos eventos van a quedar con el estado nuevo. Si hay query del grupo se
   * aplica a TODO el grupo; si no, sólo a lo cargado.
   */
  public get alcance(): string {
    if (this.data?.query && this.truncado) {
      return `los ${this.data.totalEnBase} eventos del grupo`;
    }
    return `${this.cantidadCargada} evento(s)`;
  }

  public async onSubmit(): Promise<void> {
    if (this.form.invalid) return;
    this.loading = true;
    try {
      const { nota, notaInterna, estado } = this.form.value;

      // 1. El tratamiento, con la nota. Se crea sobre los eventos cargados:
      //    es lo que el operador vio y sobre lo que está dejando constancia.
      const tratamiento: ICreateTratamientoEvento = {
        idsEventos: this.data.idsEventos,
        nota,
        notaInterna: notaInterna || undefined,
        estado,
      };
      await this.service.createGenerico(tratamiento);

      // 2. El estado del resto del grupo, si el criterio era expresable como
      //    filtro. Sin esto un grupo truncado quedaría a medio tratar.
      if (this.data.query && this.truncado) {
        // `any`: IUpdateEventoGenerico es una union discriminada que exige
        // `tipoEvento`, y el grupo cruza varios tipos de evento.
        const update: any = { estado };
        await this.eventosService.updateMany2(this.data.query, update);
      }

      this.helper.notifSuccess('Grupo tratado correctamente');
      this.dialogRef.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public cerrar(): void {
    this.dialogRef.close(false);
  }
}
